import { useQuery } from "@tanstack/react-query";
import { Volume2, VolumeX } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { hasPermission, type CurrentUser } from "../lib/auth";

const SOUND_SETTING_KEY = "apartos.sound-notifications";
const POLL_INTERVAL_MS = 10_000;
let audioContext: AudioContext | null = null;

export function SoundNotificationControl({ user }: { user: CurrentUser | null }) {
  const can = (permission: string | string[]) => hasPermission(user, permission);
  const operationalDashboard = Boolean(user && ["owner", "manager", "administrator", "concierge"].includes(user.role));
  const watchBookings = Boolean(user && can(["bookings.manage", "stays.manage", "payments.manage", "payments.collect"]));
  const watchGuestRequests = Boolean(user && can(["apartments.manage", "room_service.manage", "bookings.manage"]));
  const watchHousekeeping = Boolean(user && can(["housekeeping.manage", "housekeeping.work"]));
  const watchRoomService = Boolean(user && can(["room_service.manage", "room_service.work"]));
  const watchPayments = Boolean(user && can("payments.manage"));
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(SOUND_SETTING_KEY) !== "off";
  });

  const dashboard = useQuery({
    queryKey: ["dashboard", "summary"], queryFn: api.dashboard,
    enabled: operationalDashboard, refetchInterval: POLL_INTERVAL_MS,
  });
  const bookings = useQuery({
    queryKey: ["bookings"], queryFn: api.bookings,
    enabled: watchBookings, refetchInterval: POLL_INTERVAL_MS,
  });
  const guestRequests = useQuery({
    queryKey: ["guest-requests"], queryFn: api.guestRequests,
    enabled: watchGuestRequests, refetchInterval: POLL_INTERVAL_MS,
  });
  const housekeeping = useQuery({
    queryKey: ["housekeeping"], queryFn: api.housekeeping,
    enabled: watchHousekeeping, refetchInterval: POLL_INTERVAL_MS,
  });
  const roomService = useQuery({
    queryKey: ["room-service"], queryFn: api.roomService,
    enabled: watchRoomService, refetchInterval: POLL_INTERVAL_MS,
  });
  const payments = useQuery({
    queryKey: ["payments"], queryFn: api.payments,
    enabled: watchPayments, refetchInterval: POLL_INTERVAL_MS,
  });

  const ready = [
    !operationalDashboard || dashboard.isFetched,
    !watchBookings || bookings.isFetched,
    !watchGuestRequests || guestRequests.isFetched,
    !watchHousekeeping || housekeeping.isFetched,
    !watchRoomService || roomService.isFetched,
    !watchPayments || payments.isFetched,
  ].every(Boolean);

  const fingerprint = useMemo(() => buildSoundFingerprint({
    attention: dashboard.data?.attention.map((item) => [item.id, item.kind, item.tone, item.title, item.subtitle]) ?? [],
    bookings: bookings.data?.map((item) => [item.id, item.status, item.paid, item.total, item.apartmentNumber, item.checkInAt, item.checkOutAt]) ?? [],
    guestRequests: guestRequests.data?.map((item) => [item.id, item.status, item.kind, item.action, item.quantity, item.message]) ?? [],
    housekeeping: housekeeping.data?.map((item) => [item.id, item.status, item.priority, item.assigneeId, item.dueAt, item.checklistDone, item.checklistTotal]) ?? [],
    roomService: roomService.data?.map((item) => [item.id, item.status, item.assigneeId, item.quantity, item.dueAt]) ?? [],
    payments: payments.data?.map((item) => [item.id, item.status, item.amount, item.method, item.processedAt]) ?? [],
  }), [dashboard.data, bookings.data, guestRequests.data, housekeeping.data, roomService.data, payments.data]);

  const previousFingerprint = useRef<string | null>(null);
  const lastSoundAt = useRef(0);

  useEffect(() => {
    if (!ready) return;
    if (previousFingerprint.current === null) {
      previousFingerprint.current = fingerprint;
      return;
    }
    if (previousFingerprint.current === fingerprint) return;
    previousFingerprint.current = fingerprint;
    if (!enabled) return;
    const now = Date.now();
    if (now - lastSoundAt.current < 1_200) return;
    lastSoundAt.current = now;
    void playNotificationChime();
  }, [enabled, fingerprint, ready]);

  useEffect(() => {
    if (!enabled) return;
    const prime = () => { void primeNotificationAudio(); };
    window.addEventListener("pointerdown", prime, { once: true });
    window.addEventListener("keydown", prime, { once: true });
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
    };
  }, [enabled]);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    window.localStorage.setItem(SOUND_SETTING_KEY, next ? "on" : "off");
    if (next) void playNotificationChime();
  };

  return <button
    type="button"
    className={`icon-button sound-notification-toggle ${enabled ? "is-enabled" : ""}`}
    onClick={toggle}
    aria-label={enabled ? "Отключить звуковые уведомления" : "Включить звуковые уведомления"}
    title={enabled ? "Звуковые уведомления включены" : "Звуковые уведомления выключены"}
  >
    {enabled ? <Volume2 size={17} /> : <VolumeX size={17} />}
    {enabled && <i />}
  </button>;
}

export function buildSoundFingerprint(groups: Record<string, Array<ReadonlyArray<unknown>>>): string {
  return Object.entries(groups)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, rows]) => `${key}:${rows.map((row) => JSON.stringify(row)).sort().join("|")}`)
    .join("||");
}

async function primeNotificationAudio() {
  const context = getAudioContext();
  if (context.state === "suspended") await context.resume();
}

async function playNotificationChime() {
  try {
    await primeNotificationAudio();
    const context = getAudioContext();
    const start = context.currentTime;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.16, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.42);
    gain.connect(context.destination);

    for (const [frequency, offset] of [[659.25, 0], [880, 0.12]] as const) {
      const oscillator = context.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, start + offset);
      oscillator.connect(gain);
      oscillator.start(start + offset);
      oscillator.stop(start + 0.42);
    }
  } catch {
    // Browsers may refuse audio before the first user gesture. The next click
    // primes the same context, so notifications start without interrupting CRM work.
  }
}

function getAudioContext(): AudioContext {
  if (audioContext) return audioContext;
  const AudioContextConstructor = window.AudioContext
    ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) throw new Error("AudioContext is unavailable");
  audioContext = new AudioContextConstructor();
  return audioContext;
}
